import { flow } from 'fp-ts/function';
import { createContext, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';

export type Substitutes = Record<string, string | number>;

export interface LocalizeTextProps<S extends Record<string, unknown> = Substitutes> {
    textKey: Mews.TextKey | Mews.TextKeyUnion;
    substitutes?: S;
}

interface UseLocalizedTextParams extends LocalizeTextProps {
    fallbackToKey?: boolean;
}

const toLowerWithFirstUpper = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

const splitToWords = (textKey: Mews.TextKey | Mews.TextKeyUnion) => textKey.split(/(?=[A-Z])/).join(' ');

type GetDefaultMessage = (textKey: Mews.TextKey | Mews.TextKeyUnion, substitutes?: Substitutes) => string;

const getDefaultMessage: GetDefaultMessage = flow(splitToWords, toLowerWithFirstUpper);

export const getDefaultIntlDebugMessage: GetDefaultMessage = (
    textKey: Mews.TextKey | Mews.TextKeyUnion,
    substitutes?: Substitutes
) =>
    `:${textKey}${
        substitutes != null
            ? `(${Object.keys(substitutes)
                  .map((key) => `$${key}`)
                  .join(', ')})`
            : ''
    }`;

export const TRANSLATION_NOT_FOUND = '__TRANSLATION_NOT_FOUND__';
const MISSING_TEXT_KEY = '__MISSING_TEXT_KEY__PLEASE_ADD_ME__';

export const IntlDefaultMessageContext = createContext<GetDefaultMessage>(getDefaultMessage);

export const useLocalizeText = () => {
    const intl = useIntl();
    const defaultMessage = useContext(IntlDefaultMessageContext);

    return useCallback(
        ({ textKey, substitutes, fallbackToKey = true }: UseLocalizedTextParams) => {
            if (!textKey) {
                /** we don't have a good way to figure out where the key is missing
                 * so we will return a placeholder with call to action */
                return process.env.NODE_ENV === 'development' ? MISSING_TEXT_KEY : '';
            }

            return intl.formatMessage(
                {
                    id: textKey as Mews.TextKey,
                    defaultMessage: fallbackToKey ? defaultMessage(textKey, substitutes) : TRANSLATION_NOT_FOUND,
                },
                substitutes
            );
        },
        [intl, defaultMessage]
    );
};
